import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';

/**
 * Reading entity refs off `spec`, which is typed as `JsonObject` — every value
 * arrives as `unknown` and has to be narrowed before use.
 *
 * Two forms, and the distinction matters:
 *
 * - `shortRef` is for DISPLAY. "group:default/platform" -> "platform".
 * - `fullRef` is for COMPARISON. The catalog writes owner and system refs in
 *   whichever shorthand the YAML author used, so "guests", "group:guests" and
 *   "group:default/guests" are the same group spelled three ways. Only the
 *   expanded form compares reliably against `ownershipEntityRefs` or against
 *   another entity's `stringifyEntityRef`.
 */

/** Narrow an unknown spec value to a string, or empty. */
export const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** "group:default/platform" -> "platform", for display. */
export const shortRef = (v: unknown) =>
  str(v)
    .replace(/^[a-z]+:/, '')
    .replace(/^default\//, '');

/**
 * Normalise an owner/system value to a full entity ref for comparisons.
 *
 * Returns empty rather than throwing on a ref the catalog will not parse —
 * a malformed `spec.owner` should drop one entity out of a filter, not take
 * the whole page down.
 */
export const fullRef = (v: unknown, defaultKind: string) => {
  const s = str(v);
  if (!s) return '';
  try {
    return stringifyEntityRef(
      parseEntityRef(s, { defaultKind, defaultNamespace: 'default' }),
    );
  } catch {
    return '';
  }
};
